"use client"

import { useEffect } from "react"
import { Badge } from "@/components/ui/badge"
import { StarRating } from "@/components/star-rating"
import {
  reviewTagsFor,
  reviewTagSentiment,
  type ReviewTargetType,
} from "@/lib/badges/review-tags"
import { useTranslations } from "next-intl"

interface ReviewTagPickerProps {
  targetType: ReviewTargetType
  rating: number
  onRatingChange: (rating: number) => void
  selectedTags: Set<string> | string[]
  onToggleTag: (slug: string) => void
  disabled?: boolean
  dense?: boolean
}

function selectedHas(selectedTags: Set<string> | string[], slug: string) {
  return Array.isArray(selectedTags) ? selectedTags.includes(slug) : selectedTags.has(slug)
}

function visibleSentiments(rating: number) {
  if (rating >= 4) return new Set(["positive"])
  if (rating > 0 && rating <= 2) return new Set(["negative"])
  return new Set(["positive", "negative"])
}

export function ReviewTagPicker({
  targetType,
  rating,
  onRatingChange,
  selectedTags,
  onToggleTag,
  disabled = false,
  dense = false,
}: ReviewTagPickerProps) {
  const t = useTranslations("reviewTagPicker")
  const allowedSentiments = visibleSentiments(rating)
  const tags = reviewTagsFor(targetType).filter((tag) => allowedSentiments.has(reviewTagSentiment(tag)))

  useEffect(() => {
    if (disabled) return
    const visibleSlugs = new Set(tags.map((tag) => tag.slug))
    const selected = Array.isArray(selectedTags) ? selectedTags : Array.from(selectedTags)
    for (const slug of selected) {
      if (!visibleSlugs.has(slug)) onToggleTag(slug)
    }
  }, [disabled, onToggleTag, rating, selectedTags, tags])

  return (
    <div className={dense ? "space-y-2" : "space-y-3"}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium">{t("leaveAReview")}</p>
          <p className="text-[11px] text-muted-foreground">
            {rating >= 4
              ? "What worked well?"
              : rating > 0 && rating <= 2
                ? "What needs work?"
                : "Pick a rating, then tap anything that applies."}
          </p>
        </div>
        <StarRating rating={rating} onRatingChange={disabled ? () => {} : onRatingChange} size="md" />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {tags.map((tag) => {
          const isSelected = selectedHas(selectedTags, tag.slug)
          return (
            <Badge
              key={tag.slug}
              variant={isSelected ? "default" : "outline"}
              className={`select-none px-2.5 py-1 text-[11px] ${disabled ? "opacity-60" : "cursor-pointer"}`}
              onClick={() => {
                if (!disabled) onToggleTag(tag.slug)
              }}
            >
              {tag.label}
            </Badge>
          )
        })}
      </div>
    </div>
  )
}
